/**
 * Automations.tsx — FR-015 / T316.
 *
 * The wired surface for the automation dry-run orphan
 * (specs/exploration/stub_orphan_inventory.md §5 orphan #7):
 *
 *   - api.automations.dryRun  (mutation; requireEditor → admin/editor)
 *   - api.automations.list    (read-only query; context)
 *
 * The dryRun mutation simulates a single automation against a sample
 * payload without persisting an automationRuns row (per PRD §S1 Gap G).
 * Routed through useMutationWithToast (FR-013 (H1) / FR-015 (O-D)) so
 * argument KEYS only flow to the log sink — never the sample payload
 * contents themselves.
 *
 * The list section is re-exported from `_bundle.tsx` for layout
 * continuity; this file adds the live dry-run trigger.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react';
import { useQuery } from 'convex/react';
import { api } from '@convex/api';
import { AccessGate, Btn, Field, Panel, Select, Textarea } from '../../components/primitives';
import { useMutationWithToast } from '../../hooks/useMutationWithToast';
import { Automations as AutomationsShell } from './_bundle';

interface AutomationRow {
  _id: string;
  name: string;
  enabled?: boolean;
}

interface DryRunOutcome {
  kind: string;
  status: 'ok' | 'error';
  error?: string;
  durationMs: number;
}

export function Automations() {
  const list = useQuery(api.automations.list, {}) as AutomationRow[] | undefined;

  const dryRunM = useMutationWithToast(api.automations.dryRun, {
    logLabel: 'automations_dryRun',
    successMessage: 'Simulación completada.',
    errorMessagePrefix: 'No se pudo simular la automatización',
  });

  const [selected, setSelected] = useState<string>('');
  const [sample, setSample] = useState<string>('{}');
  const [results, setResults] = useState<DryRunOutcome[] | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);

  const handleDryRun = async () => {
    if (!selected) return;
    let payload: unknown;
    try {
      payload = JSON.parse(sample);
      setParseError(null);
    } catch (e: any) {
      setParseError(String(e?.message ?? e));
      return;
    }
    try {
      const out = (await dryRunM.run({
        id: selected as any,
        samplePayload: payload,
      })) as { results?: DryRunOutcome[] } | DryRunOutcome[];
      const rows = Array.isArray(out) ? out : (out?.results ?? []);
      setResults(rows);
    } catch {
      setResults(null);
    }
  };

  return (
    <div>
      <AutomationsShell />
      <Panel title="Simular automatización (dry-run)" style={{ marginTop: 24 }}>
        <p
          style={{
            margin: '0 0 16px',
            fontSize: 14,
            color: 'var(--body-muted)',
          }}
        >
          Llama a <code>api.automations.dryRun</code> contra una automatización
          existente sin escribir <code>automationRuns</code>. Útil para validar
          un cambio antes de habilitarlo.
        </p>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 16,
            marginBottom: 16,
          }}
        >
          <Field label="Automatización">
            <Select
              value={selected}
              onChange={setSelected}
              full
              options={(list ?? []).map((a) => ({ value: a._id, label: a.name }))}
            />
          </Field>
        </div>
        <Field
          label="Datos de muestra (JSON)"
          hint="Se envía al servicio tal cual; el envoltorio no registra el contenido."
          error={parseError ?? undefined}
        >
          <Textarea value={sample} onChange={setSample} rows={6} />
        </Field>
        <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
          <AccessGate perm="automation_edit">
            <Btn
              variant="solid"
              icon="play"
              disabled={!selected || dryRunM.loading}
              onClick={() => void handleDryRun()}
            >
              Ejecutar dry-run
            </Btn>
          </AccessGate>
        </div>
        {results !== null && (
          <div style={{ marginTop: 18 }}>
            <div
              style={{
                fontSize: 12,
                color: 'var(--muted-slate)',
                marginBottom: 6,
              }}
            >
              Resultados
            </div>
            {results.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--body-muted)' }}>
                Sin acciones evaluadas.
              </div>
            ) : (
              <table style={{ width: '100%', fontSize: 13 }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: 'var(--muted-slate)' }}>
                    <th>Acción</th>
                    <th>Estado</th>
                    <th>Duración</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r, i) => (
                    <tr key={i}>
                      <td>{r.kind}</td>
                      <td
                        style={{
                          color: r.status === 'ok' ? 'var(--good)' : 'var(--bad)',
                        }}
                      >
                        {r.status}
                        {r.error ? ` — ${r.error}` : ''}
                      </td>
                      <td
                        className="t-mono"
                        style={{ color: 'var(--muted-slate)' }}
                      >
                        {r.durationMs} ms
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </Panel>
    </div>
  );
}

export default Automations;
